import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import { formatFixed, formatIsoSeconds } from '../../utils/format';
import { GeoSample } from '../../utils/payload';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons/Icons';
import './RawDataTable.scss';

const PAGE_SIZE = 15;

interface RawDataTableProps {
  samples: GeoSample[];
  selected: number | null;
  onSelect: (index: number | null) => void;
}

const RawDataTable = ({ samples, selected, onSelect }: RawDataTableProps) => {
  const { t } = useTranslation('geo');
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(samples.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  // Rows run newest first, while `samples` (and the indices shared with the map) stay oldest first.
  const toSample = (row: number) => samples.length - 1 - row;

  useEffect(() => setPage(0), [samples]);

  // A point picked on the map brings its row into view.
  useEffect(() => {
    if (selected !== null) setPage(Math.floor((samples.length - 1 - selected) / PAGE_SIZE));
  }, [selected, samples.length]);

  const rows = samples
    .slice(Math.max(0, samples.length - (current + 1) * PAGE_SIZE), samples.length - current * PAGE_SIZE)
    .reverse();

  return (
    <section aria-label={t('raw.label')} className="RawDataTable">
      <div className="RawDataTable__head">
        <div>
          <h2>{t('raw.title')}</h2>
          <span>{t('raw.rows', { count: samples.length })}</span>
        </div>
        <div className="RawDataTable__pager">
          <button
            type="button"
            aria-label={t('raw.prev')}
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            <ChevronLeftIcon />
          </button>
          <span>{t('raw.page', { page: current + 1, pages })}</span>
          <button
            type="button"
            aria-label={t('raw.next')}
            disabled={current >= pages - 1}
            onClick={() => setPage(current + 1)}
          >
            <ChevronRightIcon size={16} />
          </button>
        </div>
      </div>
      <div className="RawDataTable__scroll">
        <table>
          <thead>
            <tr>
              {(['time', 'latitude', 'longitude', 'battery'] as const).map((c) => (
                <th key={c} scope="col">
                  {t(`raw.columns.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s, k) => {
              const i = toSample(current * PAGE_SIZE + k);
              return (
                <tr
                  key={`${s.time.getTime()}-${i}`}
                  className={selected === i ? 'RawDataTable__row--selected' : undefined}
                  onMouseEnter={() => selected !== i && onSelect(i)}
                >
                  <td>{formatIsoSeconds(s.time)}</td>
                  <td className="num">{s.latitude.toFixed(6)}</td>
                  <td className="num">{s.longitude.toFixed(6)}</td>
                  <td className="num">{s.battery === undefined ? '—' : `${formatFixed(s.battery, 0)} %`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default RawDataTable;
